import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Constants ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  new:                    { label: 'Neu eingegangen',           bg: 'bg-gray-100',    text: 'text-gray-700',    border: 'border-gray-300',    dot: 'bg-gray-400'    },
  not_reached:            { label: 'Nicht erreicht',             bg: 'bg-yellow-100',  text: 'text-yellow-800',  border: 'border-yellow-300',  dot: 'bg-yellow-500'  },
  not_suitable:           { label: 'Nicht geeignet (Vorq.)',     bg: 'bg-red-100',     text: 'text-red-800',     border: 'border-red-300',     dot: 'bg-red-400'     },
  zoom_scheduled:         { label: 'Zoom Termin geplant',        bg: 'bg-purple-100',  text: 'text-purple-800',  border: 'border-purple-300',  dot: 'bg-purple-500'  },
  in_consideration:       { label: 'In Überlegung',              bg: 'bg-indigo-100',  text: 'text-indigo-800',  border: 'border-indigo-300',  dot: 'bg-indigo-500'  },
  not_suitable_after_zoom:{ label: 'Nicht geeignet (nach Zoom)', bg: 'bg-orange-100',  text: 'text-orange-800',  border: 'border-orange-300',  dot: 'bg-orange-500'  },
  converted:              { label: 'In Akademie übernommen',    bg: 'bg-teal-100',    text: 'text-teal-800',    border: 'border-teal-300',    dot: 'bg-teal-500'    },
};

const NOTE_TYPES = {
  general:       'Allgemein',
  qualification: 'Vorqualifizierung',
  internal:      'Intern',
};

const PLATFORMS = ['Instagram', 'Facebook', 'TikTok', 'LinkedIn', 'YouTube', 'WhatsApp', 'Sonstige'];

const StatusBadge = ({ status, size = 'sm' }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium text-${size === 'sm' ? 'xs' : 'sm'} ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ─── In Überlegung Form ───────────────────────────────────────────────────────

const InConsiderationForm = ({ lead, refresh }) => {
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().slice(0, 10);

  const handleSet = async () => {
    if (!date) { toast.error('Bitte Wiedervorlage-Datum auswählen'); return; }
    setSaving(true);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${lead.id}/status`, {
        status: 'in_consideration',
        wiedervorlage_date: date,
        comment: `Wiedervorlage am ${new Date(date + 'T12:00:00').toLocaleDateString('de-DE')}`
      });
      toast.success('Lead auf "In Überlegung" gesetzt');
      await refresh();
    } catch { toast.error('Fehler beim Setzen des Status'); }
    setSaving(false);
  };

  return (
    <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-4">
      <h4 className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-2">Lead braucht Bedenkzeit?</h4>
      <p className="text-xs text-indigo-600 mb-3">Setze ein Wiedervorlage-Datum – du bekommst automatisch eine Benachrichtigung wenn das Datum erreicht ist.</p>
      <div className="flex gap-2">
        <input type="date" value={date} min={minDateStr} onChange={e => setDate(e.target.value)}
          className="flex-1 border border-indigo-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
          data-testid="wiedervorlage-date-input" />
        <button onClick={handleSet} disabled={saving || !date}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
          data-testid="set-consideration-btn">
          {saving ? 'Wird gesetzt...' : 'In Überlegung →'}
        </button>
      </div>
    </div>
  );
};

// ─── Lead Detail Modal ──────────────────────────────────────────────────────

const LeadDetailModal = ({ lead: initialLead, role, assignees, onClose, onUpdate }) => {
  const [lead, setLead] = useState(initialLead);
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [addingNote, setAddingNote] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState(lead.status);
  const [statusComment, setStatusComment] = useState('');
  const [convertConfirm, setConvertConfirm] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertManagedBy, setConvertManagedBy] = useState('');
  const [editingZoom, setEditingZoom] = useState(false);
  const [zoomForm, setZoomForm] = useState(lead.zoom_meeting || { date: '', time: '', person: '', link: '', status: 'planned' });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Auto-Zoom scheduling state
  const [autoZoomForm, setAutoZoomForm] = useState({ date: '', time: '', person: lead.assigned_to_name || '', duration: '60' });
  const [creatingAutoZoom, setCreatingAutoZoom] = useState(false);
  const [slotConflicts, setSlotConflicts] = useState(null);
  const [checkingSlot, setCheckingSlot] = useState(false);

  const canEdit = role === 'admin' || role === 'backoffice';
  const canCreate = role === 'admin' || role === 'backoffice' || role === 'vertriebsleiter';
  const canAddNote = role === 'admin' || role === 'backoffice' || role === 'vertriebsleiter';
  const canScheduleZoom = role === 'admin' || role === 'backoffice';
  const canConvert = role === 'admin' || role === 'vertriebsleiter';

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/leads/${lead.id}`);
      setLead(r.data);
      onUpdate(r.data);
    } catch {}
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/leads/${lead.id}/notes`, { text: newNote, type: noteType });
      setNewNote('');
      toast.success('Notiz hinzugefügt');
      await refresh();
    } catch { toast.error('Fehler beim Hinzufügen der Notiz'); }
    setAddingNote(false);
  };

  const handleStatusChange = async () => {
    if (newStatus === lead.status) { setChangingStatus(false); return; }
    try {
      await axios.put(`${API}/leads/${lead.id}/status`, { status: newStatus, comment: statusComment });
      toast.success('Status aktualisiert');
      setChangingStatus(false);
      setStatusComment('');
      await refresh();
    } catch { toast.error('Fehler beim Aktualisieren des Status'); }
  };

  const handleFieldSave = async (field) => {
    try {
      await axios.put(`${API}/leads/${lead.id}`, { [field]: fieldValue });
      setEditingField(null);
      toast.success('Gespeichert');
      await refresh();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleSaveZoom = async () => {
    try {
      const zm = { date: zoomForm.date, time: zoomForm.time, person: zoomForm.person, link: zoomForm.link, status: zoomForm.status };
      await axios.put(`${API}/leads/${lead.id}`, { zoom_meeting: zm });
      // auto set status to zoom_scheduled if meeting set
      if (zm.date && lead.status === 'zoom_scheduled') {
        await axios.put(`${API}/leads/${lead.id}/status`, { status: 'zoom_scheduled', comment: 'Zoom Termin eingetragen' });
      }
      toast.success('Zoom Termin gespeichert');
      setEditingZoom(false);
      await refresh();
    } catch { toast.error('Fehler beim Speichern des Termins'); }
  };

  const handleConvert = async () => {
    if (!convertManagedBy) {
      toast.error('Bitte wähle einen Teamleiter aus – jeder Vertriebler muss einem Teamleiter zugeordnet sein!');
      return;
    }
    if (!convertConfirm) { setConvertConfirm(true); return; }
    setConverting(true);
    setConvertConfirm(false);
    try {
      const r = await axios.post(`${API}/leads/${lead.id}/convert`, { managed_by: convertManagedBy });
      toast.success(r.data.message || 'Account erfolgreich erstellt!');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler bei der Konvertierung'); }
    setConverting(false);
  };

  const handleSendZoomEmail = async () => {
    if (!lead.email) { toast.error('Lead hat keine E-Mail-Adresse'); return; }
    setSendingEmail(true);
    try {
      const zm = lead.zoom_meeting;
      const zoomDate = zm?.date ? `${zm.date}${zm.time ? ' um ' + zm.time + ' Uhr' : ''}` : 'noch nicht festgelegt';
      await axios.post(`${API}/leads/${lead.id}/notes`, {
        text: `E-Mail-Bestätigung gesendet an ${lead.email} mit Zoom-Termin: ${zoomDate}`,
        type: 'internal'
      });
      toast.success(`Bestätigung an ${lead.email} notiert`);
      await refresh();
    } catch { toast.error('Fehler'); }
    setSendingEmail(false);
  };

  // Auto-Zoom: check for conflicts when date/time changes
  const checkSlotConflicts = async (date, time) => {
    if (!date || !time) { setSlotConflicts(null); return; }
    setCheckingSlot(true);
    try {
      const res = await axios.get(`${API}/leads/check-slot`, { params: { date, time, duration: autoZoomForm.duration || 60 } });
      setSlotConflicts(res.data.conflicts || []);
    } catch { setSlotConflicts(null); }
    setCheckingSlot(false);
  };

  const handleAutoZoomFormChange = (field, value) => {
    const updated = { ...autoZoomForm, [field]: value };
    setAutoZoomForm(updated);
    if (field === 'date' || field === 'time') {
      checkSlotConflicts(updated.date, updated.time);
    }
  };

  const handleCreateAutoZoom = async () => {
    if (!autoZoomForm.date || !autoZoomForm.time) { toast.error('Datum und Uhrzeit sind erforderlich'); return; }
    if (!lead.email) {
      toast('Lead hat keine E-Mail-Adresse – kein Bestätigungs-E-Mail wird versendet.', { icon: '⚠️' });
    }
    setCreatingAutoZoom(true);
    try {
      const res = await axios.post(`${API}/leads/${lead.id}/schedule-zoom`, {
        date: autoZoomForm.date,
        time: autoZoomForm.time,
        person: autoZoomForm.person,
        duration: parseInt(autoZoomForm.duration, 10) || 60,
      });
      toast.success('Zoom-Termin erstellt! Bestätigungs-E-Mail wurde versendet.');
      setLead(res.data);
      onUpdate(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Erstellen des Zoom-Termins'); }
    setCreatingAutoZoom(false);
  };

  const EditableField = ({ label, field, value, type = 'text' }) => (
    <div>
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      {editingField === field ? (
        <div className="flex gap-2">
          <input type={type} value={fieldValue} onChange={e => setFieldValue(e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button onClick={() => handleFieldSave(field)} className="text-xs bg-blue-600 text-white px-2.5 py-1.5 rounded-lg">✓</button>
          <button onClick={() => setEditingField(null)} className="text-xs text-gray-400 px-2 py-1.5 rounded-lg hover:bg-gray-100">✕</button>
        </div>
      ) : (
        <div className="flex items-center gap-2 group">
          <span className="text-sm text-gray-800">{value || <span className="text-gray-300 italic">–</span>}</span>
          {canEdit && (
            <button onClick={() => { setEditingField(field); setFieldValue(value || ''); }}
              className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-blue-500 transition-opacity">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white font-bold">
              {(lead.first_name || '?').charAt(0)}
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">{lead.first_name} {lead.last_name}</h2>
              <p className="text-slate-300 text-xs">{lead.email} · {lead.phone}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={lead.status} size="sm" />
            <button onClick={onClose} className="text-white/60 hover:text-white p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-100 px-6 flex gap-1 flex-shrink-0 bg-white">
          {[
            { key: 'overview', label: 'Übersicht' },
            { key: 'notes', label: `Notizen (${(lead.notes || []).length})` },
            { key: 'zoom', label: 'Zoom Termin' },
            { key: 'history', label: 'Verlauf' },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {/* Contact Info */}
              <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-4">
                <EditableField label="Vorname" field="first_name" value={lead.first_name} />
                <EditableField label="Nachname" field="last_name" value={lead.last_name} />
                <EditableField label="Telefon" field="phone" value={lead.phone} />
                <EditableField label="E-Mail" field="email" value={lead.email} type="email" />
                <EditableField label="Quelle / Plattform" field="source" value={lead.source} />
                <EditableField label="Kampagne" field="campaign" value={lead.campaign} />
              </div>

              {/* Status + Datum */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Eingangsdatum</p>
                  <p className="text-sm text-gray-800">{new Date(lead.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                </div>
              </div>

              {/* Status Change */}
              {canEdit && (
                <div className="border border-gray-100 rounded-xl p-4">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Status ändern</h4>
                  <div className="flex gap-2">
                    <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {Object.entries(STATUS_CONFIG)
                        .filter(([k]) => k !== 'zoom_scheduled' && k !== 'converted' && k !== 'not_suitable_after_zoom')
                        .map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                    <button onClick={handleStatusChange}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                      Setzen
                    </button>
                  </div>
                </div>
              )}

              {/* Post-Zoom: In Überlegung setzen (VL / Admin, nur bei zoom_scheduled) */}
              {(role === 'vertriebsleiter' || role === 'admin') && lead.status === 'zoom_scheduled' && !lead.academy_user_id && (
                <InConsiderationForm lead={lead} refresh={refresh} />
              )}

              {/* In Überlegung: Wiedervorlage-Info anzeigen */}
              {lead.status === 'in_consideration' && lead.wiedervorlage_date && (
                <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  <div>
                    <p className="text-sm font-semibold text-indigo-800">Wiedervorlage am {new Date(lead.wiedervorlage_date + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                    <p className="text-xs text-indigo-600 mt-0.5">Lead überlegt noch. Benachrichtigung wird automatisch ausgelöst.</p>
                  </div>
                </div>
              )}

              {/* Post-Zoom: Nicht geeignet (VL / Admin) */}
              {(role === 'vertriebsleiter' || role === 'admin') && (lead.status === 'zoom_scheduled' || lead.status === 'in_consideration') && !lead.academy_user_id && (
                <div className="border border-orange-200 bg-orange-50 rounded-xl p-4">
                  <h4 className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-2">Zoom-Ergebnis: Nicht geeignet?</h4>
                  <p className="text-xs text-orange-600 mb-3">Lead hat sich nach dem Zoom-Gespräch als nicht geeignet herausgestellt.</p>
                  <button
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/leads/${lead.id}/status`, { status: 'not_suitable_after_zoom', comment: 'Nicht geeignet nach Zoom-Gespräch' });
                        toast.success('Status aktualisiert');
                        await refresh();
                      } catch { toast.error('Fehler beim Aktualisieren'); }
                    }}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2 rounded-lg text-sm font-semibold transition-colors"
                    data-testid="not-suitable-after-zoom-btn">
                    Nicht geeignet nach Zoom markieren
                  </button>
                </div>
              )}

              {/* Convert to Academy */}
              {canConvert && (lead.status === 'zoom_scheduled' || lead.status === 'in_consideration') && !lead.academy_user_id && (
                <div className="border-2 border-teal-200 bg-teal-50 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-teal-800 mb-1">In Akademie übernehmen</h4>
                  <p className="text-xs text-teal-600 mb-3">Erstellt automatisch einen Vertriebler-Account und sendet eine Setup-E-Mail an {lead.email}.</p>
                  {assignees.filter(a => a.role === 'teamleiter').length === 0 && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                      Kein aktiver Teamleiter im System gefunden.
                    </p>
                  )}
                  <div className="flex gap-2">
                    <select value={convertManagedBy} onChange={e => { setConvertManagedBy(e.target.value); setConvertConfirm(false); }}
                      className="flex-1 border border-teal-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                      data-testid="convert-teamleiter-select">
                      <option value="">-- Teamleiter zuweisen (Pflichtfeld)</option>
                      {assignees.filter(a => a.role === 'teamleiter').map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                    {!convertConfirm ? (
                      <button onClick={handleConvert} disabled={converting || !convertManagedBy}
                        title={!convertManagedBy ? 'Bitte Teamleiter auswählen' : ''}
                        className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap"
                        data-testid="convert-lead-btn">
                        Account anlegen →
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={handleConvert} disabled={converting}
                          className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap"
                          data-testid="convert-confirm-btn">
                          {converting ? 'Wird erstellt...' : 'Ja, umwandeln ✓'}
                        </button>
                        <button onClick={() => setConvertConfirm(false)}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          data-testid="convert-cancel-btn">
                          Abbrechen
                        </button>
                      </div>
                    )}
                  </div>
                  {convertConfirm && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                      Bist du sicher? Ein Vertriebler-Account für <strong>{lead.first_name} {lead.last_name}</strong> wird angelegt und eine Setup-E-Mail versendet.
                    </p>
                  )}
                </div>
              )}
              {lead.academy_user_id && (
                <div className="border border-teal-200 bg-teal-50 rounded-xl p-4 flex items-center gap-3">
                  <svg className="w-5 h-5 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                  <div>
                    <p className="text-sm font-semibold text-teal-800">In Akademie übernommen</p>
                    <p className="text-xs text-teal-600">Konvertiert von {lead.converted_by} · {lead.converted_at ? new Date(lead.converted_at).toLocaleDateString('de-DE') : ''}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Notes ── */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              {canAddNote && (
                <div className="border border-gray-100 rounded-xl p-4">
                  <div className="flex gap-2 mb-3">
                    {Object.entries(NOTE_TYPES).map(([k, v]) => (
                      <button key={k} onClick={() => setNoteType(k)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${noteType === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                  <textarea value={newNote} onChange={e => setNewNote(e.target.value)} rows={3}
                    placeholder="Notiz eingeben..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  <button onClick={handleAddNote} disabled={addingNote || !newNote.trim()}
                    className="mt-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    {addingNote ? 'Wird gespeichert...' : 'Notiz hinzufügen'}
                  </button>
                </div>
              )}
              <div className="space-y-3">
                {[...(lead.notes || [])].reverse().map(note => (
                  <div key={note.id} className="border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-700">{note.author_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          note.type === 'qualification' ? 'bg-indigo-100 text-indigo-700' :
                          note.type === 'internal' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                          {NOTE_TYPES[note.type] || 'Allgemein'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{new Date(note.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.text}</p>
                  </div>
                ))}
                {(lead.notes || []).length === 0 && <p className="text-sm text-gray-400 text-center py-4">Noch keine Notizen vorhanden.</p>}
              </div>
            </div>
          )}

          {/* ── Zoom ── */}
          {activeTab === 'zoom' && (
            <div className="space-y-4">
              {/* ── Auto-created meeting display ── */}
              {lead.zoom_meeting?.auto_created && !editingZoom ? (
                <div className="space-y-3">
                  {/* Success Banner */}
                  <div className="border border-purple-200 bg-purple-50 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                        </div>
                        <h4 className="text-sm font-bold text-purple-800">Zoom Termin (automatisch erstellt)</h4>
                      </div>
                      {canScheduleZoom && <button onClick={() => setEditingZoom(true)} className="text-xs text-purple-600 hover:text-purple-800 font-medium border border-purple-300 px-2 py-1 rounded-lg">Bearbeiten</button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {[
                        { label: 'Datum', value: lead.zoom_meeting.date ? new Date(lead.zoom_meeting.date + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : '–' },
                        { label: 'Uhrzeit', value: lead.zoom_meeting.time ? lead.zoom_meeting.time + ' Uhr' : '–' },
                        { label: 'Zuständige Person', value: lead.zoom_meeting.person || '–' },
                        { label: 'Status', value: lead.zoom_meeting.status === 'completed' ? 'Durchgeführt' : lead.zoom_meeting.status === 'cancelled' ? 'Abgesagt' : 'Geplant' },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <p className="text-xs text-purple-500 mb-0.5">{label}</p>
                          <p className="text-sm font-medium text-purple-900">{value}</p>
                        </div>
                      ))}
                    </div>
                    {lead.zoom_meeting.link && (
                      <div className="bg-white border border-purple-200 rounded-lg p-3 flex items-center gap-3">
                        <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>
                        <a href={lead.zoom_meeting.link} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline break-all flex-1">{lead.zoom_meeting.link}</a>
                        <button onClick={() => { navigator.clipboard.writeText(lead.zoom_meeting.link); toast.success('Link kopiert!'); }}
                          className="text-xs text-gray-400 hover:text-gray-700 border border-gray-200 px-2 py-1 rounded transition-colors flex-shrink-0">Kopieren</button>
                      </div>
                    )}
                    {lead.zoom_meeting.zoom_password && (
                      <p className="text-xs text-purple-600 mt-2">Passwort: <span className="font-mono font-bold">{lead.zoom_meeting.zoom_password}</span></p>
                    )}
                    {lead.zoom_meeting.gcal_event_id && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        <span className="text-xs text-green-700 font-medium">In Google Kalender eingetragen</span>
                      </div>
                    )}
                  </div>

                  {/* Reminders info */}
                  <div className="border border-amber-100 bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-3">
                    <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
                    <div>
                      <p className="text-xs font-semibold text-amber-800">Automatische Erinnerungen</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        {lead.reminder_24h_sent ? '✓ 24h-Erinnerung gesendet' : '• 24h vorher: Erinnerungs-E-Mail an Lead'}
                        {' · '}
                        {lead.reminder_1h_sent ? '✓ 1h-Erinnerung gesendet' : '• 1h vorher: Erinnerungs-E-Mail an Lead'}
                      </p>
                    </div>
                  </div>
                </div>

              ) : lead.zoom_meeting && !lead.zoom_meeting.auto_created && !editingZoom ? (
                /* Manually entered meeting display */
                <div className="border border-purple-200 bg-purple-50 rounded-xl p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-purple-800">Zoom Termin (manuell)</h4>
                    {canEdit && <button onClick={() => setEditingZoom(true)} className="text-xs text-purple-600 hover:text-purple-800 font-medium">Bearbeiten</button>}
                  </div>
                  {[
                    { label: 'Datum', value: lead.zoom_meeting.date },
                    { label: 'Uhrzeit', value: lead.zoom_meeting.time ? lead.zoom_meeting.time + ' Uhr' : null },
                    { label: 'Zuständige Person', value: lead.zoom_meeting.person },
                    { label: 'Meeting Link', value: lead.zoom_meeting.link, link: true },
                    { label: 'Status', value: lead.zoom_meeting.status === 'completed' ? 'Durchgeführt' : lead.zoom_meeting.status === 'cancelled' ? 'Abgesagt' : 'Geplant' },
                  ].map(({ label, value, link }) => value ? (
                    <div key={label} className="flex gap-4">
                      <span className="text-xs text-purple-500 w-36 flex-shrink-0">{label}</span>
                      {link ? (
                        <a href={value} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline break-all">{value}</a>
                      ) : (
                        <span className="text-sm font-medium text-purple-900">{value}</span>
                      )}
                    </div>
                  ) : null)}
                  {canEdit && lead.email && (
                    <button onClick={handleSendZoomEmail} disabled={sendingEmail}
                      className="mt-2 w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium transition-colors">
                      {sendingEmail ? 'Wird gesendet...' : 'E-Mail Bestätigung an Lead senden'}
                    </button>
                  )}
                </div>

              ) : canScheduleZoom ? (
                /* ── Auto-create or edit form ── */
                <div className="space-y-4">
                  {/* Auto-Erstellungs-Form */}
                  {!editingZoom && (
                    <div className="border-2 border-blue-200 bg-blue-50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-blue-800">Zoom Termin automatisch erstellen</h4>
                          <p className="text-xs text-blue-600">Erstellt Zoom-Link + Google Kalender + E-Mail-Bestätigung automatisch</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Datum *</label>
                          <input type="date" value={autoZoomForm.date} min={new Date().toISOString().slice(0,10)}
                            onChange={e => handleAutoZoomFormChange('date', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Uhrzeit *</label>
                          <input type="time" value={autoZoomForm.time}
                            onChange={e => handleAutoZoomFormChange('time', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Zuständige Person</label>
                          <input type="text" value={autoZoomForm.person} placeholder="z.B. Salem Rmeihi"
                            onChange={e => handleAutoZoomFormChange('person', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Dauer (Minuten)</label>
                          <select value={autoZoomForm.duration} onChange={e => setAutoZoomForm({ ...autoZoomForm, duration: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                            {['30', '45', '60', '90'].map(d => <option key={d} value={d}>{d} Min.</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Conflict Warning */}
                      {checkingSlot && <p className="text-xs text-gray-400 animate-pulse mb-2">Prüfe Konflikte...</p>}
                      {slotConflicts && slotConflicts.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-start gap-2">
                          <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                          <div>
                            <p className="text-xs font-semibold text-red-700">Terminkonflikte gefunden</p>
                            {slotConflicts.map((c, i) => (
                              <p key={`conflict-${c.time || i}`} className="text-xs text-red-600">Akademie-Abschlussgespräch um {c.time} Uhr ({c.duration} Min.)</p>
                            ))}
                          </div>
                        </div>
                      )}
                      {slotConflicts && slotConflicts.length === 0 && autoZoomForm.date && autoZoomForm.time && (
                        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
                          <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                          <p className="text-xs text-green-700 font-medium">Kein Konflikt mit Akademie-Terminen</p>
                        </div>
                      )}

                      {!lead.email && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
                          <svg className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                          <p className="text-xs text-yellow-700">Kein E-Mail – Bestätigungsmail kann nicht versendet werden</p>
                        </div>
                      )}

                      <button onClick={handleCreateAutoZoom} disabled={creatingAutoZoom || !autoZoomForm.date || !autoZoomForm.time}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-bold transition-colors"
                        data-testid="auto-zoom-create-btn">
                        {creatingAutoZoom ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Zoom wird erstellt...
                          </span>
                        ) : 'Zoom Termin erstellen + E-Mail senden'}
                      </button>
                    </div>
                  )}

                  {/* Edit existing meeting */}
                  {editingZoom && (
                    <div className="border border-gray-100 rounded-xl p-5 space-y-3">
                      <h4 className="text-sm font-bold text-gray-700 mb-3">Termin bearbeiten</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Datum</label>
                          <input type="date" value={zoomForm.date} onChange={e => setZoomForm({ ...zoomForm, date: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Uhrzeit</label>
                          <input type="time" value={zoomForm.time} onChange={e => setZoomForm({ ...zoomForm, time: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Zuständige Person</label>
                          <input type="text" value={zoomForm.person} onChange={e => setZoomForm({ ...zoomForm, person: e.target.value })}
                            placeholder="Name der Person"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Termin Status</label>
                          <select value={zoomForm.status} onChange={e => setZoomForm({ ...zoomForm, status: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="planned">Geplant</option>
                            <option value="completed">Durchgeführt</option>
                            <option value="cancelled">Abgesagt</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Meeting Link</label>
                        <input type="url" value={zoomForm.link} onChange={e => setZoomForm({ ...zoomForm, link: e.target.value })}
                          placeholder="https://zoom.us/j/..."
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={handleSaveZoom} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">Speichern</button>
                        <button onClick={() => setEditingZoom(false)} className="text-gray-400 px-4 py-2 rounded-lg text-sm hover:bg-gray-100 transition-colors">Abbrechen</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">Noch kein Zoom Termin eingetragen.</p>
              )}
            </div>
          )}

          {/* ── History ── */}
          {activeTab === 'history' && (
            <div className="space-y-2">
              {[...(lead.status_history || [])].reverse().map((entry, i) => (
                <div key={entry.at ? `hist-${entry.at}-${i}` : i} className="flex gap-3 items-start">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                    {i < (lead.status_history || []).length - 1 && <div className="w-0.5 bg-gray-100 flex-1 mt-1" style={{ minHeight: '20px' }} />}
                  </div>
                  <div className="pb-3 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {entry.to && <StatusBadge status={entry.to} />}
                      <span className="text-xs text-gray-400">{entry.by} · {new Date(entry.at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {entry.comment && <p className="text-xs text-gray-500 mt-1">{entry.comment}</p>}
                  </div>
                </div>
              ))}
              {(lead.status_history || []).length === 0 && <p className="text-sm text-gray-400 text-center py-4">Keine Historie vorhanden.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Create Lead Modal ──────────────────────────────────────────────────────

const CreateLeadModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', source: '', campaign: '', platform: 'Instagram' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone) { toast.error('Vorname und Telefon sind Pflichtfelder'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/leads`, { ...form, source: form.source || form.platform });
      toast.success('Lead erfolgreich angelegt!');
      onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Anlegen'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-white font-bold">Neuer Lead</h2>
          <button onClick={onClose} className="text-white/60 hover:text-white"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Vorname *', field: 'first_name', required: true },
              { label: 'Nachname', field: 'last_name' },
              { label: 'Telefon *', field: 'phone', required: true },
              { label: 'E-Mail', field: 'email', type: 'email' },
            ].map(({ label, field, required, type }) => (
              <div key={field}>
                <label className="text-xs text-gray-500 block mb-1">{label}</label>
                <input type={type || 'text'} required={required} value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Plattform</label>
              <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value, source: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Kampagne</label>
              <input type="text" value={form.campaign} onChange={e => setForm({ ...form, campaign: e.target.value })}
                placeholder="z.B. Sommer 2026"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-bold transition-colors">
              {saving ? 'Wird angelegt...' : 'Lead anlegen'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-gray-500 hover:bg-gray-100 rounded-lg text-sm transition-colors">Abbrechen</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main LeadsPage Component ───────────────────────────────────────────────

export default function LeadsPage({ role }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [assignees, setAssignees] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  // filterSuitable removed (simplified status workflow)

  const canEdit = role === 'admin' || role === 'backoffice';
  const canCreate = role === 'admin' || role === 'backoffice' || role === 'vertriebsleiter';
  const isVL = role === 'vertriebsleiter';
  const VL_STATUSES = ['zoom_scheduled', 'in_consideration', 'not_suitable_after_zoom', 'converted'];
  const visibleStatuses = isVL ? VL_STATUSES : Object.keys(STATUS_CONFIG);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterSource) params.source = filterSource;
      const [leadsRes, assigneesRes] = await Promise.all([
        axios.get(`${API}/leads`, { params }),
        axios.get(`${API}/leads-meta/assignees`),
      ]);
      setLeads(leadsRes.data || []);
      setAssignees(assigneesRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, filterStatus, filterSource]);

  useEffect(() => { load(); }, [load]);

  const handleLeadUpdate = (updated) => {
    setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
    if (selectedLead === updated.id) { /* already refreshed in modal */ }
  };

  // Stats
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    not_reached: leads.filter(l => l.status === 'not_reached').length,
    not_suitable: leads.filter(l => l.status === 'not_suitable').length,
    zoom_scheduled: leads.filter(l => l.status === 'zoom_scheduled').length,
    converted: leads.filter(l => l.status === 'converted').length,
  };

  const selectedLeadData = leads.find(l => l.id === selectedLead);

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Media Leads</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vorqualifizierung und Übergabe an die Akademie</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm"
            data-testid="create-lead-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
            Neuer Lead
          </button>
        )}
      </div>

      {/* ── KPI Row ── */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        {[
          { label: 'Gesamt', value: stats.total, color: 'gray' },
          { label: 'Neu', value: stats.new, color: 'blue' },
          { label: 'Nicht erreicht', value: stats.not_reached, color: 'yellow' },
          { label: 'Zoom geplant', value: stats.zoom_scheduled, color: 'purple' },
          { label: 'In Akademie', value: stats.converted, color: 'teal' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 text-center">
            <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            <input type="text" placeholder="Name, Telefon oder E-Mail..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="lead-search" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="lead-status-filter">
            <option value="">Alle Status</option>
            {visibleStatuses.map(k => <option key={k} value={k}>{STATUS_CONFIG[k]?.label}</option>)}
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Alle Plattformen</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {(search || filterStatus || filterSource) && (
            <button onClick={() => { setSearch(''); setFilterStatus(''); setFilterSource(''); }}
              className="text-xs text-gray-400 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              Filter zurücksetzen
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-2">{leads.length} Lead{leads.length !== 1 ? 's' : ''} gefunden</p>
      </div>

      {/* ── Lead Table ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-400">Leads werden geladen...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="py-16 text-center">
            <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <p className="text-sm font-medium text-gray-400">Noch keine Leads vorhanden</p>
            {canCreate && <button onClick={() => setShowCreate(true)} className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium">Ersten Lead anlegen →</button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Name', 'Kontakt', 'Plattform', 'Eingangsdatum', 'Status', 'Zoom', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {leads.map(lead => {
                  const cfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                  const isConverted = lead.status === 'converted';
                  const hasZoom = !!lead.zoom_meeting?.date;
                  return (
                    <tr key={lead.id}
                      className={`hover:bg-gray-50 transition-colors ${isConverted ? 'opacity-60' : ''} ${lead.status === 'not_suitable' || lead.status === 'not_suitable_after_zoom' ? 'bg-red-50/30' : ''}`}
                      data-testid={`lead-row-${lead.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{lead.first_name} {lead.last_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-700">{lead.phone}</p>
                        <p className="text-xs text-gray-400">{lead.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{lead.source || lead.platform || '–'}</span>
                        {lead.campaign && <p className="text-xs text-gray-400 mt-0.5">{lead.campaign}</p>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleDateString('de-DE')}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                      <td className="px-4 py-3">
                        {hasZoom ? (
                          <div className="flex items-center gap-1 text-xs text-purple-700">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                            {lead.zoom_meeting.date}
                          </div>
                        ) : <span className="text-xs text-gray-300">–</span>}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => setSelectedLead(lead.id)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2.5 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                          data-testid={`lead-detail-btn-${lead.id}`}>
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      {selectedLead && selectedLeadData && (
        <LeadDetailModal
          lead={selectedLeadData}
          role={role}
          assignees={assignees}
          onClose={() => setSelectedLead(null)}
          onUpdate={(updated) => {
            handleLeadUpdate(updated);
            setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
          }}
        />
      )}

      {/* ── Create Modal ── */}
      {showCreate && (
        <CreateLeadModal
          onClose={() => setShowCreate(false)}
          onCreated={(lead) => { setLeads(prev => [lead, ...prev]); setShowCreate(false); }}
        />
      )}
    </div>
  );
}
